/**
 * Copyright (C) Boost commerce
 * This file is part of commercial Boost commerce projects
 *
 * This file can not be copied and/or distributed without the express
 * permission of Boost commerce
 *
 * Created on Fri Jun 30 2023 11:09:43
 */
// @flow

import _ from 'lodash';
import classnames from 'classnames';
import { getShop } from 'src/helpers';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import React, { Component, Fragment } from 'react';
import { reset, Field, reduxForm, getFormValues } from 'redux-form';
import SmileIcon from 'src/assets/images/csat-feedback/smile-icon.svg';
import CloseIcon from 'src/assets/images/csat-feedback/close-icon.svg';
import PoorIcon from 'src/assets/images/csat-feedback/poor-icon.svg';
import OkIcon from 'src/assets/images/csat-feedback/ok-icon.svg';
import GoodIcon from 'src/assets/images/csat-feedback/good-icon.svg';
import ExcellentIcon from 'src/assets/images/csat-feedback/excellent-icon.svg';
import DecorIcon from 'src/assets/images/csat-feedback/decor.svg';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { FormLayout, Button } from '@shopify/polaris';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import {
  getCSATAdminAppDisplay,
  closeCSATFeedbackWidget,
  sendCSATFeedback
} from 'stores/actions/csat-feedback-action';
import { URL } from 'src/constants/links';
import moment from 'moment/moment';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';

const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
const triggerDelay = isProduction ? 5 * 60 * 1000 : 2 * 60 * 1000;

type Props = {};

type StarName = 'poor' | 'ok' | 'good' | 'excellent';
type StepName = 'rating' | 'feedback' | 'submitted';

type State = {
  eventSources: Array<string>,
  isExpanded: boolean,
  feedbackTitle: string,
  feedbackSubtitle: string,
  starName: StarName,
  badMood: boolean,
  stepName: StepName,
  timeoutId: NodeJS.Timeout,
  eventTracking: {
    CSATType: 'Floating Button' | 'Bubble Popup'
  }
};

type ComposedProps = Props & WithI18nProps;

class CSATFeedback extends Component<ComposedProps, State> {
  state = {
    eventSources: null,
    badMood: false,
    isExpanded: false,
    feedbackTitle: null,
    feedbackSubtitle: null,
    starName: null,
    timeoutId: null,
    stepName: 'rating',
    alreadyRateExcellent: false,
    eventTracking: {}
  };

  async componentDidMount() {
    // Delay show popup 5m
    this.getNewStatus(triggerDelay, true);

    // Check rating before
    this.checkRatingBefore();

    // Listening event from user click
    Emitter.on(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY, () => this.getNewStatus(triggerDelay));
  }

  /**
   * Check store already rate excellent before
   */
  checkRatingBefore = () => {
    let hadRateExcellent = false;
    try {
      hadRateExcellent = localStorage.getItem(`${getShop()}-hadRateExcellentApp`);
    } catch (error) {}

    // If the banner is dismissed, hide it
    if (hadRateExcellent === 'true') {
      this.setState({ alreadyRateExcellent: true });
    }
  };

  /**
   * Process when store rate status excellent
   */
  processRateExcellent = () => {
    try {
      localStorage.setItem(`${getShop()}-hadRateExcellentApp`, true);
    } catch (error) {}
    this.setState({ alreadyRateExcellent: true });
    window.open(URL['ShopifyStoreReviewLink'], '_blank');
  };

  /**
   * Get new status of csat feedback widget
   * @param {*} delay Wait for delay time
   * @param {*} allowOverride Can override setTimeout existing to make a new one
   */
  getNewStatus = (delay: number, allowOverride: boolean = false) => {
    const { getCSATAdminAppDisplay } = this.props;

    if (this.state.timeoutId) {
      if (!allowOverride) return;
      clearTimeout(this.state.timeoutId);
    }

    const timeoutId = setTimeout(async () => {
      const { isExpanded } = this.state;

      // No need to call API if customer has just clicked sticky button
      if (isExpanded) return;

      // If the widget has been showed today, need to wait until tomorrow for next time
      const isAllowed = this.allowToShowWidget();
      if (!isAllowed) return;

      const response = await getCSATAdminAppDisplay();
      const payload = response?.payload?.data;

      // Ignore if any error occurs
      if (_.isEmpty(payload)) return;

      const isShow = payload?.isShow;
      const isMultiple = payload?.isMultiple;
      const eventSources = payload?.metadata?.eventSources;

      if (isShow) {
        this.saveLastDisplayTime();

        const eventSource = _.head(eventSources);
        const { title, subtitle } = this.getContentByEvent(eventSource, isMultiple);

        // Expand popup for customer give feedback
        this.setState({
          eventSources,
          timeoutId: null,
          isExpanded: true,
          feedbackTitle: title,
          feedbackSubtitle: subtitle,
          eventTracking: { CSATType: 'Bubble Popup' }
        });

        //Tracking event
        mixpanelV3Helper.trackEventData('Display CSAT Popup', {
          pagePath: window.location.pathname,
          feature: 'CSAT',
          CSATType: 'Bubble Popup',
          triggerEvent: eventSource
        });
      } else {
        this.setState({
          timeoutId: null,
          eventSources: null,
          isExpanded: false,
          feedbackTitle: null,
          feedbackSubtitle: null
        });
      }
    }, delay);

    // Save for debouncing function call when customer clicks to many times
    this.setState({ timeoutId });
  };

  /**
   * Check the last time the widget show for customer
   */
  allowToShowWidget = () => {
    try {
      const tenantId = getShop();
      const lastShow = localStorage.getItem(tenantId + '-CSATFeedback');
      return moment().diff(_.toNumber(lastShow), 'day') > 0;
    } catch (error) {
      console.log('allowToShowWidget() failed');
      return true;
    }
  };

  /**
   * Save to local storage the last time the widget show for customer
   */
  saveLastDisplayTime = () => {
    try {
      const tenantId = getShop();
      localStorage.setItem(tenantId + '-CSATFeedback', Date.now());
    } catch (error) {
      // console.log('saveLastDisplayTime() failed');
    }
  };

  /**
   * Expand feedback widget by click sticky button
   */
  expandFeedbackWidget = () => {
    const { isExpanded } = this.state;
    if (isExpanded) return;

    const { title, subtitle } = this.getContentByEvent();
    this.setState({
      isExpanded: true,
      feedbackTitle: title,
      feedbackSubtitle: subtitle,
      eventTracking: { CSATType: 'Floating Button' }
    });

    //Tracking event
    mixpanelV3Helper.trackEventData('Click on CSAT Popup', {
      pagePath: window.location.pathname,
      feature: 'CSAT',
      CSATType: 'Floating Button'
    });
  };

  /**
   * Send feedback to admin api
   */
  sendFeedback = async (noContent: boolean) => {
    const { sendCSATFeedback, formValues } = this.props;
    const { starName, eventSources, eventTracking } = this.state;
    const message = noContent ? '' : _.get(formValues, 'content', '');

    // Send feedback
    const feedback = { rating: starName, eventSources, message };
    await sendCSATFeedback(feedback);

    // For testing
    if (!isProduction) {
      this.getNewStatus(5 * 60 * 1000);
    }

    // Tracking event
    mixpanelV3Helper.trackEventData('Rate on CSAT Popup', {
      pagePath: window.location.pathname,
      feature: 'CSAT',
      CSATType: eventTracking.CSATType,
      rate: starName
    });
  };

  /**
   * Collapse feedback widget, when customer click no thanks or close button on corner
   */
  collapseFeedbackWidget = (remindMeLater: boolean) => {
    const { isExpanded, stepName, eventSources } = this.state;
    if (!isExpanded) return;

    this.setState({ isExpanded: false });

    const { resetForm, closeCSATFeedbackWidget } = this.props;

    // Customer don't want to spam
    if (!remindMeLater) {
      const closeBody = { action: 'closed', sources: eventSources };
      if (stepName === 'rating') {
        closeCSATFeedbackWidget(closeBody);
      } else if (stepName === 'feedback') {
        // Close widget in feedback step, send feedback without message content
        this.sendFeedback(true);
      }
    }

    // Reset content of feedback after cancel
    resetForm();

    // Delay 300 ms for avoiding flash content
    setTimeout(() => {
      this.setState({ stepName: 'rating', starName: null, eventSources: null });
    }, 300);
  };

  /**
   * Click submit form feedback form
   */
  submit = () => {
    this.sendFeedback();
    this.setState({ stepName: 'submitted' });
  };

  /**
   * Go to next step after rating
   * @param {*} starName
   */
  rate = (starName: StarName) => {
    const { tenantApp } = this.props;
    const { alreadyRateExcellent } = this.state;

    const clientReview = _.get(tenantApp, 'clientReview', 0);
    const hasReview = clientReview && clientReview !== 'none';

    const badMood = ['poor', 'ok'].includes(starName);
    const stepName = badMood ? 'feedback' : 'submitted';
    this.setState({ starName, badMood, stepName });

    // If rate excellent => open new tab
    if (starName === 'excellent' && !alreadyRateExcellent && !hasReview) {
      this.processRateExcellent();
    }

    if (!badMood) {
      // To get last state
      setTimeout(() => {
        this.sendFeedback();
      }, 100);
    }
  };

  /**
   * Get random content when user click on sticky button
   * @returns
   */
  getRandomContent = () => {
    const randomContent = [
      {
        title: 'AdminHTML.CSATFeedback.FeedbackWidget.RandomContent01_Title',
        subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.RandomContent01_Subtitle'
      },
      {
        title: 'AdminHTML.CSATFeedback.FeedbackWidget.RandomContent02_Title',
        subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.RandomContent02_Subtitle'
      },
      {
        title: 'AdminHTML.CSATFeedback.FeedbackWidget.RandomContent03_Title',
        subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.RandomContent03_Subtitle'
      }
    ];

    return randomContent[Date.now() % 3];
  };

  /**
   * Get content to display on feedback widget by event source
   * If eventSource is passed to function, return content by eventSource
   * Else return random content for floating button click
   * @param {*} eventSource
   * @returns
   */
  getContentByEvent = (eventSource: string, isMultiple: boolean) => {
    if (isMultiple) {
      return {
        title: 'AdminHTML.CSATFeedback.FeedbackWidget.CommonContent_Title',
        subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.CommonContent_Subtitle'
      };
    }

    switch (eventSource) {
      case 'filter-trees':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.FilterTreeContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.FilterTreeContent_Subtitle'
        };
      case 'payment':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.PaymentContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.PaymentContent_Subtitle'
        };
      case 'recommendation':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.RecommendationContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.RecommendationContent_Subtitle'
        };
      case 'theme-setup':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.ThemeSetupContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.ThemeSetupContent_Subtitle'
        };
      case 'analytics':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.AnalyticContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.AnalyticContent_Subtitle'
        };
      case 'sync':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.SyncContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.SyncContent_Subtitle'
        };
      case 'merchandising':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.MerchandisingContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.MerchandisingContent_Subtitle'
        };
      case 'search':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.SearchContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.SearchContent_Subtitle'
        };
      case 'translation':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.TranslationContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.TranslationContent_Subtitle'
        };
      case 'metafields':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.MetafieldsContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.MetafieldsContent_Subtitle'
        };
      case 'variants-display':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.VariantDisplayContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.VariantDisplayContent_Subtitle'
        };
      case 'sort-by-options':
        return {
          title: 'AdminHTML.CSATFeedback.FeedbackWidget.SortByOptionContent_Title',
          subtitle: 'AdminHTML.CSATFeedback.FeedbackWidget.SortByOptionContent_Subtitle'
        };
      default:
        return this.getRandomContent();
    }
  };

  render() {
    const { i18n, pristine } = this.props;
    const { isExpanded, stepName, feedbackSubtitle, feedbackTitle, badMood, eventSources } =
      this.state;

    return (
      <div className={classnames('BC-CSATFeedback__Wrapper', { expanded: isExpanded })}>
        <div
          className={classnames('StickyButton', { clickable: !isExpanded })}
          onClick={this.expandFeedbackWidget}>
          {!isExpanded && (
            <div className="StickyButton__Title">
              {i18n.translate('AdminHTML.CSATFeedback.StickyButton.title')}
            </div>
          )}
          <img className="StickyButton__Icon" src={SmileIcon} alt="smile-icon-on-sticky-button" />
        </div>
        <div className={classnames('FeedbackWidget', { visible: isExpanded })}>
          <img
            className="FeedbackWidget__CloseButton"
            src={CloseIcon}
            alt="close-widget-icon"
            onClick={() => this.collapseFeedbackWidget(false)}
          />
          {stepName === 'rating' ? (
            <div className="FeedbackWidget__Rating">
              <div
                className={classnames('Rating__Container', { reverse: !_.isEmpty(eventSources) })}>
                <div className="Rating__Title">
                  {feedbackTitle ? i18n.translate(feedbackTitle) : null}
                </div>
                <div className="Rating__Subtitle">
                  {feedbackSubtitle ? i18n.translate(feedbackSubtitle) : null}
                </div>
              </div>
              <div className="Rating__Stars">
                <div className={classnames('Star__Container Poor', { animation: isExpanded })}>
                  <img src={PoorIcon} alt="poor-icon" onClick={() => this.rate('poor')} />
                  <div className="Star__Name">
                    {i18n.translate('AdminHTML.CSATFeedback.Icon.Poor')}
                  </div>
                </div>
                <div className={classnames('Star__Container Ok', { animation: isExpanded })}>
                  <img src={OkIcon} alt="ok-icon" onClick={() => this.rate('ok')} />
                  <div className="Star__Name">
                    {i18n.translate('AdminHTML.CSATFeedback.Icon.Ok')}
                  </div>
                </div>
                <div className={classnames('Star__Container Good', { animation: isExpanded })}>
                  <img src={GoodIcon} alt="good-icon" onClick={() => this.rate('good')} />
                  <div className="Star__Name">
                    {i18n.translate('AdminHTML.CSATFeedback.Icon.Good')}
                  </div>
                </div>
                <div className={classnames('Star__Container Excellent', { animation: isExpanded })}>
                  <img
                    src={ExcellentIcon}
                    alt="excellent-icon"
                    onClick={() => this.rate('excellent')}
                  />
                  <div className="Star__Name">
                    {i18n.translate('AdminHTML.CSATFeedback.Icon.Excellent')}
                  </div>
                </div>
              </div>
              {!_.isEmpty(eventSources) && (
                <div className="Rating__Action">
                  <Button removeUnderline plain onClick={() => this.collapseFeedbackWidget(true)}>
                    {i18n.translate('AdminHTML.CSATFeedback.FeedbackWidget.IgnoreText')}
                  </Button>
                </div>
              )}
            </div>
          ) : stepName === 'feedback' ? (
            <div className="FeedbackWidget__Feedback">
              <div className="Feedback__Title">
                {i18n.translate('AdminHTML.CSATFeedback.FeedbackWidget.FeedbackTitle')}
              </div>
              <div className="Feedback__Textarea">
                <FormLayout>
                  <Field
                    name="content"
                    component={RenderTextField}
                    placeholder={i18n.translate(
                      'AdminHTML.CSATFeedback.FeedbackWidget.FeedbackPlaceholder'
                    )}
                    anotherProps={{ multiline: 4 }}
                  />
                </FormLayout>
                <div className="Feedback__Action">
                  <Button onClick={() => this.collapseFeedbackWidget(false)}>
                    {i18n.translate('AdminHTML.CSATFeedback.Action.No')}
                  </Button>
                  <Button primary disabled={pristine} onClick={this.submit}>
                    {i18n.translate('AdminHTML.CSATFeedback.Action.Send')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="FeedbackWidget__GotIt">
              <img className="GotIt__Decor" src={DecorIcon} alt="decor-background-got-it-screen" />
              <div className="GotIt__Title">
                {i18n.translate('AdminHTML.CSATFeedback.GotIt.Title')}
              </div>
              {badMood ? (
                <Fragment>
                  <div className="GotIt__Subtitle">
                    {i18n.translate('AdminHTML.CSATFeedback.GotIt.BadMood_Subtitle1', {
                      t1: (
                        <strong>
                          {i18n.translate('AdminHTML.CSATFeedback.GotIt.BadMood_Subtitle1_t1')}
                        </strong>
                      )
                    })}
                  </div>
                  <div className="GotIt__Subtitle">
                    {i18n.translate('AdminHTML.CSATFeedback.GotIt.BadMood_Subtitle2', {
                      t1: (
                        <strong>
                          {i18n.translate('AdminHTML.CSATFeedback.GotIt.BadMood_Subtitle2_t1')}
                        </strong>
                      )
                    })}
                  </div>
                </Fragment>
              ) : (
                <div className="GotIt__Subtitle">
                  {i18n.translate('AdminHTML.CSATFeedback.GotIt.GoodMood_Subtitle')}
                </div>
              )}
              <div className="GotIt__Action">
                <Button primary onClick={() => this.collapseFeedbackWidget(false)}>
                  {i18n.translate('AdminHTML.CSATFeedback.Action.GotIt')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const formValues = getFormValues('CSATFeedbackForm')(state);

  return { tenantApp, formValues };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  resetForm: () => dispatch(reset('CSATFeedbackForm')),
  closeCSATFeedbackWidget: body => dispatch(closeCSATFeedbackWidget(body, ownProps.i18n)),
  getCSATAdminAppDisplay: () => dispatch(getCSATAdminAppDisplay(ownProps.i18n)),
  sendCSATFeedback: body => dispatch(sendCSATFeedback(body, ownProps.i18n))
});

const CSATFeedbackForm = reduxForm({
  form: 'CSATFeedbackForm',
  enableReinitialize: true
})(CSATFeedback);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(CSATFeedbackForm));
